// externals
import { RSAA } from 'redux-api-middleware';
import { nanoid } from 'nanoid';

// libraries
import type { RSAACallAction, RSAACallRequest } from '@makemydeal/dr-react-18-wrappers';
import type { DealHistory, StateTree } from '@makemydeal/dr-dash-types';
import type { BffApiUrlResult } from '@makemydeal/dr-shared-ui-utils';
import { apiConfigHelper, locHrefUtil, MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { bootstrapSharedActionCreators, selectedOfferSelectors } from '@makemydeal/dr-shared-store';

// consts/enums
import type {
    DealHistoryCheckedAction,
    DealHistoryUnCheckedAction,
    DealHistorySelectedUpdate
} from '../actionTypes/dealHistoryActionTypes';
import {
    FETCH_DEAL_HISTORY_REQUEST,
    FETCH_DEAL_HISTORY_SUCCESS,
    FETCH_DEAL_HISTORY_FAILURE,
    DEAL_HISTORY_CHECKED,
    DEAL_HISTORY_UNCHECKED,
    DEAL_HISTORY_SELECTED_UPDATE,
    SET_DEAL_HISTORY_SCENARIO_DRAWER_DEAL,
    DEAL_HISTORY_OPEN_DRAWER,
    DEAL_HISTORY_CLOSE_DRAWER,
    SET_MAKE_CURRENT_PENDING,
    CLEAR_MAKE_CURRENT_SUCCESS
} from '../actionTypes/dealHistoryActionTypes';
import {
    MAKE_CURRENT,
    FETCH_DEALER_REVIEW_BAGGAGE_REQUEST,
    FETCH_DEALER_REVIEW_BAGGAGE_SUCCESS,
    FETCH_DEALER_REVIEW_BAGGAGE_FAILURE
} from '../actionTypes/offerActionTypes';
import type { DashStoreThunk } from '../types/appState';
import type { RealState } from '@makemydeal/shared-scoped-store';
import { scopedActions, scopedActionsCreators, scopedStateSelectors } from '@makemydeal/shared-scoped-store';

type RsaaType = string | { type: string; meta: Record<string, unknown> };

const buildSelectedDealGet = (state: StateTree, resource: string, types: [RsaaType, RsaaType, RsaaType]): RSAACallAction => {
    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult: BffApiUrlResult = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, '');
    const dealXgId = selectedOfferSelectors.getSelectedOfferDealXgId(state);
    const request: RSAACallRequest = {
        endpoint: `${bffApiUrlResult.url}/${resource}/${dealXgId}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        types
    };
    return {
        [RSAA]: request
    } as RSAACallAction;
};

export const fetchDealHistory = (state: StateTree): RSAACallAction =>
    buildSelectedDealGet(state, 'offerHistoryDxg', [
        FETCH_DEAL_HISTORY_REQUEST,
        FETCH_DEAL_HISTORY_SUCCESS,
        FETCH_DEAL_HISTORY_FAILURE
    ]);

export const fetchDealerReviewBaggage = (state: StateTree, announcedVersion = ''): RSAACallAction =>
    buildSelectedDealGet(state, 'dealBaggageDxg', [
        FETCH_DEALER_REVIEW_BAGGAGE_REQUEST,
        { type: FETCH_DEALER_REVIEW_BAGGAGE_SUCCESS, meta: { announcedVersion } },
        FETCH_DEALER_REVIEW_BAGGAGE_FAILURE
    ]);

export const dealHistoryChecked = (deal: DealHistory): DealHistoryCheckedAction => ({ type: DEAL_HISTORY_CHECKED, payload: deal });

export const dealHistoryUnchecked = (offerId: string): DealHistoryUnCheckedAction => ({
    type: DEAL_HISTORY_UNCHECKED,
    payload: offerId
});

export const dealHistorySelectedUpdate = (offerId: string): DealHistorySelectedUpdate => ({
    type: DEAL_HISTORY_SELECTED_UPDATE,
    payload: offerId
});

export const setDealHistoryScenariosDrawerDeal = (deal: DealHistory | undefined) => ({
    type: SET_DEAL_HISTORY_SCENARIO_DRAWER_DEAL,
    payload: deal
});

export const openDealHistoryDrawer = () => ({
    type: DEAL_HISTORY_OPEN_DRAWER
});

export const closeDealHistoryDrawer = () => ({
    type: DEAL_HISTORY_CLOSE_DRAWER
});

export const setMakeCurrentPending = () => ({
    type: SET_MAKE_CURRENT_PENDING
});

export const clearMakeCurrentSuccess = () => ({
    type: CLEAR_MAKE_CURRENT_SUCCESS
});

export const makeCurrentDeal =
    (dealXgVersion?: string): DashStoreThunk =>
    (dispatch, getState) => {
        const state = getState();

        const primaryScopeId = scopedStateSelectors.getPrimaryScopeId(state as unknown as RealState);
        const newPrimaryScopeId = nanoid();

        // Copy the primary scope to a new one with a different scope id to avoid collision with scope id from the previous deal version
        dispatch(scopedActionsCreators.cloneScope(primaryScopeId, {}, newPrimaryScopeId));
        dispatch(scopedActionsCreators.changeDefaultScope(newPrimaryScopeId));
        dispatch(scopedActionsCreators.setNavigationScopeTarget(newPrimaryScopeId));
        dispatch(scopedActionsCreators.deleteNonPrimaryScopes());

        dispatch(scopedActions.createScopedAction(newPrimaryScopeId, { type: FETCH_DEAL_HISTORY_REQUEST }));

        dispatch(scopedActions.createScopedAction(newPrimaryScopeId, { type: MAKE_CURRENT }));

        dispatch(
            scopedActions.createScopedAction(
                newPrimaryScopeId,
                bootstrapSharedActionCreators.fetchBootstrapData(MANAGER_VIEW_APP_PREFIX, {
                    withCurrentDealVersion: true,
                    dealXgVersion,
                    updatePrevOffer: false
                }) as any
            )
        );
    };
